/**
 * ч.1106 (рішення власника): «Видалити акаунт» — справжнє видалення, одразу і
 * безповоротно. Акаунти, де користувач — єдиний власник, видаляються з усіма
 * організаціями; з чужих акаунтів людина просто виходить. Підтвердження:
 * пошта вручну, пароль і підпис ключем КЕП КОЖНОЇ підтвердженої організації
 * в акаунтах, що видаляються.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accountSelf } from '@/api/accountOrgs';
import { leaveSession } from '@/auth/store';
import KeyAuthPanel from '@/shared/eusign/KeyAuthPanel';
import { signKepAction } from '@/shared/eusign/kepAction';

export default function DeleteAccountSection() {
  const q = useQuery({ queryKey: ['account-delete-preview'], queryFn: accountSelf.deletePreview });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pv = q.data;
  const doomed = (pv?.tenants ?? []).filter((t) => t.will_be_deleted);
  const leaving = (pv?.tenants ?? []).filter((t) => !t.will_be_deleted);
  const kepOrgs = doomed.flatMap((t) => t.organizations.filter((o) => o.kep_confirmed));
  const emailOk = !!pv && email.trim().toLowerCase() === pv.email.toLowerCase();
  const lock = pv?.blocked_reason ?? (!emailOk ? 'Введіть свою пошту для підтвердження'
    : !password ? 'Введіть пароль облікового запису' : undefined);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const signatures: Record<string, { challenge: string; signature_b64: string }> = {};
      for (const o of kepOrgs) {
        signatures[o.id] = await signKepAction('account-delete', o.id, o.code ?? '', o.name,
          () => accountSelf.kepChallenge(o.id));
      }
      await accountSelf.remove({ password, confirm_email: email, signatures });
      leaveSession();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e as Error)?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Видалити акаунт</h2>
          <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            Обліковий запис видаляється одразу і безповоротно.
          </div>
        </div>
      </div>
      {q.isLoading && <p>Завантажуємо…</p>}
      {pv && (
        <>
          <div style={{ background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', borderRadius: 8,
            padding: '0.9rem', fontSize: '0.9rem', marginBottom: '0.8rem' }} data-testid="delete-account-impact">
            {doomed.length > 0 ? (
              <>
                <b>Разом з обліковим записом буде видалено</b> бізнес-акаунти, де ви єдиний власник, — з усіма
                організаціями, документами, проводками, довідниками і вкладеннями:
                <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
                  {doomed.map((t) => (
                    <li key={t.id}>
                      «{t.name}»{t.organizations.length > 0
                        ? ` — організації: ${t.organizations.map((o) => o.name).join(', ')}`
                        : ' — без організацій'}
                      {t.other_members > 0 && `; доступ втратять користувачі акаунта: ${t.other_members}`}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: '0.4rem' }}>
                  Щоб зберегти облік — спершу передайте організації («Передача організації») або
                  вивантажте дані.
                </div>
              </>
            ) : <b>Бізнес-акаунтів, які були б видалені разом із записом, немає.</b>}
            {leaving.length > 0 && (
              <div style={{ marginTop: '0.4rem' }}>
                З акаунтів {leaving.map((t) => `«${t.name}»`).join(', ')} ви просто вийдете — їхній облік лишиться.
              </div>
            )}
          </div>
          {pv.blocked_reason && <p style={{ color: 'var(--badge-red-fg)' }}>{pv.blocked_reason}</p>}
          {kepOrgs.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem', marginBottom: '0.8rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Авторизація ключами КЕП</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Підтверджені ключем організації: {kepOrgs.map((o) => `${o.name}${o.code ? ` (${o.code})` : ''}`).join(', ')}.
                Зчитайте ключ кожної з них — видалення буде підписано цими ключами.
              </div>
              <KeyAuthPanel />
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ minWidth: 280 }}>
              <label>Для підтвердження введіть свою пошту</label>
              <input value={email} placeholder={pv.email} aria-label="Пошта для підтвердження"
                onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
            </div>
            <div className="field" style={{ width: 220 }}>
              <label>Пароль облікового запису</label>
              <input type="password" value={password} aria-label="Пароль облікового запису"
                onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
          </div>
          {error && <div style={{ color: 'var(--badge-red-fg)', marginTop: '0.5rem' }} role="alert">{error}</div>}
          <button type="button" className="danger" style={{ marginTop: '0.8rem' }}
            disabled={!!lock || busy} title={lock} onClick={() => void submit()}>
            {busy ? 'видаляємо…' : 'видалити обліковий запис назавжди'}
          </button>
        </>
      )}
    </div>
  );
}
